import React from "react";
import { Col, Container, Row, Label, Input } from "reactstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import AuthLogo from "../../Components/Auth/AuthLogo";
import AuthCard from "../../Components/Auth/AuthCard";
import AuthContainer from "../../Components/Auth/AuthContainer";
import CustomInput from "../../Components/Custom/CustomInput";
import CustomButton from "../../Components/Custom/CustomButton";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import { RegisterArgs } from "types/auth.api";
import { getFcmToken } from "helpers/firebase/fcm";
import { registerUser } from "slices/auth/thunk";
import { ErrorHandler, SuccessToast } from "utils/toast";
import {
  VALIDATION_MESSAGES,
  VALIDATION_LIMITS,
} from "Components/constants/validation";

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { isLoading } = useAppSelector((state) => state?.auth);
  const firebaseConfig = useAppSelector(
    (state) => state?.config?.firebaseConfig,
  );
  const prefilledEmail = searchParams.get("email")?.trim() || "";

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: prefilledEmail,
      password: "",
      confirmPassword: "",
      acceptTerms: false,
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .transform((value) => value?.trim())
        .required(VALIDATION_MESSAGES.REQUIRED("Your Email"))
        .email(VALIDATION_MESSAGES.INVALID_EMAIL),
      password: Yup.string()
        .trim()
        .required(VALIDATION_MESSAGES.REQUIRED("Password"))
        .test(
          "no-spaces",
          "Password cannot contain spaces",
          (value) => !value || !/\s/.test(value),
        )
        .min(
          VALIDATION_LIMITS.PASSWORD.MIN,
          VALIDATION_MESSAGES.MIN_LENGTH(
            "Password",
            VALIDATION_LIMITS.PASSWORD.MIN,
          ),
        )
        .max(
          VALIDATION_LIMITS.PASSWORD.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH(
            "Password",
            VALIDATION_LIMITS.PASSWORD.MAX,
          ),
        ),

      confirmPassword: Yup.string()
        .trim()
        .required(VALIDATION_MESSAGES.REQUIRED("Confirm Password"))
        .test(
          "no-spaces",
          "Password cannot contain spaces",
          (value) => !value || !/\s/.test(value),
        )
        .oneOf([Yup.ref("password")], "Password must match")
        .min(
          VALIDATION_LIMITS.PASSWORD.MIN,
          VALIDATION_MESSAGES.MIN_LENGTH(
            "Confirm Password",
            VALIDATION_LIMITS.PASSWORD.MIN,
          ),
        )
        .max(
          VALIDATION_LIMITS.PASSWORD.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH(
            "Confirm Password",
            VALIDATION_LIMITS.PASSWORD.MAX,
          ),
        ),
      acceptTerms: Yup.boolean()
        .oneOf(
          [true],
          "You must accept the Terms & Conditions and Privacy Policy",
        )
        .required("You must accept the Terms & Conditions and Privacy Policy"),
    }),
    onSubmit: async (values) => {
      const token = await getFcmToken();
      console.log("values", values);

      const registerPayload: RegisterArgs = {
        email: values?.email?.trim(),
        password: values?.password?.trim(),
        device_type: "web",
        push_token: token,
      };

      try {
        await dispatch(registerUser(registerPayload))?.unwrap();
        navigate("/login");
        SuccessToast(
          "Registered successfully! Please check your email to verify your account.",
        );
      } catch (error) {
        ErrorHandler(error);
      }
    },
  });

  // const handleTermsClick = (e: React.MouseEvent<HTMLSpanElement>) => {
  //   e.preventDefault();
  //   e.stopPropagation();
  // };

  // const handlePrivacyClick = (e: React.MouseEvent<HTMLSpanElement>) => {
  //   e.preventDefault();
  //   e.stopPropagation();
  // };

  document.title = "SignUp | EaziQuote";
  return (
    <React.Fragment>
      <ParticlesAuth>
        <AuthContainer>
          <Container>
            <Row>
              <Col lg={12}>
                <AuthLogo />
              </Col>
            </Row>
            <Row className="auth-container">
              <Col md={8} lg={6} xl={5}>
                <AuthCard>
                  <div className="text-center mt-2">
                    <h5 className="text-black2D font-primary fw-semibold fs-24">
                      Let’s Get Started 🚀
                    </h5>
                    <p className="font-primary fs-14 text-grey89 fw-normal">
                      Sign up and simplify your quoting and invoicing process.
                    </p>
                  </div>
                  <div className="p-2 mt-4">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                      }}
                      className="needs-validation"
                    >
                      <CustomInput
                        label="Email"
                        placeholder="Enter your email"
                        name="email"
                        type="email"
                        value={validation.values.email}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        touched={validation.touched.email}
                        error={validation.errors.email}
                        required
                      />
                      <CustomInput
                        label="Password"
                        placeholder="Enter your password"
                        name="password"
                        type="password"
                        value={validation.values.password}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        touched={validation.touched.password}
                        error={validation.errors.password}
                        isPassword={true}
                        required
                      />
                      <CustomInput
                        label="Confirm Password"
                        placeholder="Re-enter password"
                        name="confirmPassword"
                        type="password"
                        value={validation.values.confirmPassword}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        touched={validation.touched.confirmPassword}
                        error={validation.errors.confirmPassword}
                        isPassword={true}
                        required
                      />
                      <div className="form-check mb-3">
                        <div className="d-flex align-items-start">
                          <Input
                            className={`form-check-input ${
                              validation.touched.acceptTerms &&
                              validation.errors.acceptTerms
                                ? "is-invalid"
                                : ""
                            }`}
                            type="checkbox"
                            name="acceptTerms"
                            id="auth-remember-check"
                            checked={validation.values.acceptTerms}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            style={{ width: 20, height: 20, marginRight: 8 }}
                          />
                          <Label
                            className="form-check-label text-black1C fs-16 font-primary fw-normal"
                            htmlFor="auth-remember-check"
                          >
                            I agree to the{" "}
                            <a
                              href={
                                firebaseConfig?.termsOfService &&
                                firebaseConfig?.termsOfService.trim().length > 0
                                  ? firebaseConfig.termsOfService
                                  : "https://eaziquote-landing.silverskytechnology.com/terms-and-conditions/"
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="terms-link"
                            >
                              Terms & Conditions
                            </a>{" "}
                            and{" "}
                            <a
                              className="terms-link"
                              href={
                                firebaseConfig?.privacyPolicy &&
                                firebaseConfig?.privacyPolicy.trim().length > 0
                                  ? firebaseConfig.privacyPolicy
                                  : "https://eaziquote-landing.silverskytechnology.com/privacy-policy/"
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Privacy Policy
                            </a>
                          </Label>
                        </div>
                        {validation.touched.acceptTerms &&
                          validation.errors.acceptTerms && (
                            <div className="invalid-feedback d-block">
                              {validation.errors.acceptTerms}
                            </div>
                          )}
                      </div>

                      <CustomButton
                        text="Sign Up"
                        disabled={isLoading}
                        type="submit"
                        loading={isLoading}
                        className="auth-Button z-3"
                      />

                      <div className="mt-4 text-center">
                        <p className="mb-0 font-primary fs-16 fw-normal text-grey89">
                          Already have an account?{" "}
                          <Link
                            to="/login"
                            className="text-primary font-primary fs-16 fw-normal"
                          >
                            Sign In
                          </Link>
                        </p>
                      </div>
                    </form>
                  </div>
                </AuthCard>
              </Col>
            </Row>
          </Container>
        </AuthContainer>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default Register;
